import type { API_RESPONSE } from '@corefront/eva-fe-sdk';
import { createSelector } from 'reselect';
import { or, pathOr, uniqWith, eqProps, slice } from 'ramda';
import { userNumberSelector } from './params';
import {
  SET_RECENT_SEARCHED_GAME,
  SET_RECENT_LAUNCHED_GAME,
  SET_VIEWED_CAMPAIGNS,
} from '../../constants/persistStorage';
import type { RootState } from '../../store';

export const DEFAULT_USER_NUMBER = 'anonymous';
export const MAX_GAMES_COUNT = 16;

export interface PersistStorageState {
  recentLaunchedGames: Array<API_RESPONSE.GameInfo['200']['game']>;
  recentSearchedGames: Array<API_RESPONSE.GameInfo['200']['game']>;
  viewedCampaigns: Array<string>;
}

export interface PersistStorageInitialState {
  [key: string]: PersistStorageState;
}

export const initialUserState: PersistStorageState = {
  recentLaunchedGames: [],
  recentSearchedGames: [],
  viewedCampaigns: [],
};
export const initialState: PersistStorageInitialState = {
  [DEFAULT_USER_NUMBER]: initialUserState,
};

const getUserState = (state: PersistStorageInitialState, userNumber: string) =>
  pathOr(initialUserState, [userNumber], state);

const getUserRecentLaunchedGames = (
  state: PersistStorageInitialState,
  userNumber: string
) =>
  getUserState(state, userNumber).recentLaunchedGames ??
  initialUserState.recentLaunchedGames;
const getUserRecentSearchedGames = (
  state: PersistStorageInitialState,
  userNumber: string
) =>
  getUserState(state, userNumber).recentSearchedGames ??
  initialUserState.recentSearchedGames;
const getUserViewedCampaigns = (
  state: PersistStorageInitialState,
  userNumber: string
) =>
  getUserState(state, userNumber).viewedCampaigns ??
  initialUserState.viewedCampaigns;

const getUniqList = uniqWith<
  PersistStorageState['recentLaunchedGames'][number]
>(eqProps('id'));
const getSlicedList = (
  list: PersistStorageState['recentLaunchedGames'],
  limit = Infinity
) => slice(0, limit, list);
const getUniqSlicedList = (
  list: PersistStorageState['recentLaunchedGames'],
  limit: number
) => getSlicedList(getUniqList(list), limit);

const reducer = (
  state = initialState,
  action: { type: string; payload: any }
): Record<string, PersistStorageState> => {
  const userNumber = pathOr(DEFAULT_USER_NUMBER, ['userNumber'], action);
  // Return default state for no authorized users
  if (!userNumber) return state;

  switch (action.type) {
    case SET_RECENT_LAUNCHED_GAME:
      return {
        ...state,
        [userNumber]: {
          ...getUserState(state, userNumber),
          recentLaunchedGames: getUniqSlicedList(
            [action.payload, ...getUserRecentLaunchedGames(state, userNumber)],
            MAX_GAMES_COUNT
          ),
        },
      };
    case SET_RECENT_SEARCHED_GAME:
      return {
        ...state,
        [userNumber]: {
          ...getUserState(state, userNumber),
          recentSearchedGames: getUniqSlicedList(
            [action.payload, ...getUserRecentSearchedGames(state, userNumber)],
            MAX_GAMES_COUNT
          ),
        },
      };
    case SET_VIEWED_CAMPAIGNS:
      return {
        ...state,
        [userNumber]: {
          ...getUserState(state, userNumber),
          viewedCampaigns: action.payload,
        },
      };

    default:
      return state;
  }
};

export default reducer;

export const rootStateSelector = (state: RootState) =>
  state?.app?.persistStorage;

export const userNumberWithDefaultSelector = createSelector(
  userNumberSelector,
  (userNumber) => or(userNumber, DEFAULT_USER_NUMBER)
);
export const recentLaunchedGamesSelector = createSelector(
  rootStateSelector,
  userNumberWithDefaultSelector,
  (rootState, userNumber) => getUserRecentLaunchedGames(rootState, userNumber)
);
export const recentSearchedGamesSelector = createSelector(
  rootStateSelector,
  userNumberWithDefaultSelector,
  (rootState, userNumber) => getUserRecentSearchedGames(rootState, userNumber)
);
export const viewedCampaignIdsSelector = createSelector(
  rootStateSelector,
  userNumberWithDefaultSelector,
  (rootState, userNumber) =>
    new Set(getUserViewedCampaigns(rootState, userNumber))
);
